import React from 'react'
import { FormControl, Select, MenuItem, ListItemText, Icon } from '@mui/material'
import { withStyles } from '@mui/styles'
import { Tooltip, Checkbox, Typography, Box } from '@mui/material'

import { selectInputStyles } from './select-input.styles'

/**
 * @typedef {object} SelectOption
 * @property {string | number} key - El identificador único de la opción.
 * @property {string} value - El texto que se mostrará para la opción.
 * @property {boolean} [disabled] - Si es `true`, la opción estará deshabilitada.
 */

/**
 * @typedef {object} SelectInputProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 * @property {SelectOption[]} [values=[]] - Un array de opciones disponibles para seleccionar.
 * @property {boolean} [multiple=false] - Si es `true`, permite la selección de múltiples valores con checkboxes.
 * @property {boolean} [readOnly=false] - Si es `true`, muestra el valor seleccionado como texto estático.
 * @property {boolean} [selectAll=false] - Si es `true` y `multiple` es `true`, muestra una opción "Seleccionar todos".
 * @property {string} [title] - El título que se muestra sobre el selector.
 * @property {string} [tooltip] - Un texto de ayuda que se muestra en un tooltip junto al título.
 * @property {'small' | 'normal'} [fontSize] - Controla el tamaño de la fuente del título.
 * @property {string | number | Array<string|number>} [value] - El valor actualmente seleccionado. Si `multiple` es `true`, debe ser un array.
 * @property {string} name - El nombre del campo.
 * @property {function(React.ChangeEvent): void} onChange - La función callback que se ejecuta cuando la selección cambia.
 * @property {boolean} [required] - Si es `true`, el campo se marca como requerido.
 * @property {boolean} [disabled] - Si es `true`, el campo estará deshabilitado.
 */

/**
 * Un componente de selector desplegable (dropdown) con soporte para selección simple o múltiple.
 * 
 * Construido sobre `Select` de Material-UI, proporciona una interfaz consistente para seleccionar
 * opciones. En modo múltiple, muestra checkboxes y opcionalmente una opción "Seleccionar todos".
 * Incluye modo de solo lectura que muestra los valores seleccionados como texto.
 * 
 * @component
 * @example
 * const MyForm = () => {
 *   const [country, setCountry] = useState('');
 * 
 *   const countries = [
 *     { key: 'ES', value: 'España' },
 *     { key: 'FR', value: 'Francia' },
 *     { key: 'IT', value: 'Italia' },
 *   ];
 * 
 *   const handleChange = (event) => {
 *     setCountry(event.target.value);
 *   };
 * 
 *   return (
 *     <SelectInput
 *       name="country"
 *       title="País"
 *       tooltip="Seleccione su país de residencia"
 *       values={countries}
 *       value={country}
 *       onChange={handleChange}
 *     />
 *   );
 * };
 * 
 * @example
 * // Modo múltiple con "Seleccionar todos"
 * <SelectInput
 *   name="categories"
 *   title="Categorías"
 *   values={categoryOptions}
 *   value={selectedCategories}
 *   onChange={handleChange}
 *   multiple
 *   selectAll
 * />
 * 
 * @param {SelectInputProps} props - Las props del componente SelectInput.
 */
export const SelectInput = React.memo(
  withStyles(selectInputStyles)(
    ({ classes, values = [], multiple = false, readOnly = false, selectAll = false, ...props }) => {
      const typographyVariant = props.fontSize === 'small' ? '2' : '1'
      const value = props.value || (multiple ? [] : '')
      const getValue = () => {
        if (multiple) {
          let optionsSelected = []
          value.forEach((v) => {
            const optionSelected = values.find((item) => item.key === v)
            if (optionSelected) optionsSelected.push(optionSelected.value)
          })
          return optionsSelected.join(', ')
        } else {
          let optionSelected
          let valueSelected = '--'
          if (value && values) {
            optionSelected = values.find((option) => option.key === value)
            valueSelected = optionSelected ? optionSelected.value : '--'
          }
          return valueSelected
        }
      }

      const selectValues = () => {
        return values.map((item, i) => (
          <MenuItem key={i} value={item.key} disabled={item.disabled}>
            {multiple ? (
              <>
                <Checkbox checked={value.indexOf(item.key) > -1} />
                <ListItemText primary={item.value} />
              </>
            ) : (
              item.value
            )}
          </MenuItem>
        ))
      }

      const multipleProps =
        multiple && values.length
          ? {
              renderValue: (selected) =>
                selected
                  .map((item) => {
                    let found = values.find((value) => value.key === item)
                    return found ? found.value : 'Unknown'
                  })
                  .join(', '),
            }
          : {}

      const allSelected =
        multiple && values.length && value.length ? values.length === value.length : false

      const changeHandle = (e) => {
        if (selectAll && e.target.value.includes('all')) {
          return props.onChange({
            ...e,
            target: {
              ...e.target,
              value: allSelected ? [] : values.map((el) => el.key),
            },
          })
        }
        return props.onChange(e)
      }

      return (
        <FormControl
          sx={{ width: '100%' }}
          variant="outlined"
          className={classes.root}
          required={props.required}
          disabled={props.disabled}
          margin="none"
        >
          <Box display="flex" flexDirection="horizontal">
            {props.title && (
              <Typography variant={`subtitle${typographyVariant}`} className={classes.title}>
                {props.title}
              </Typography>
            )}
            {props.tooltip && (
              <span style={{ fontSize: 16, marginLeft: 4 }}>
                <Tooltip
                  placement="top"
                  title={
                    <Typography variant={`subtitle${typographyVariant}`} className={classes.title}>
                      {props.tooltip}
                    </Typography>
                  }
                >
                  <Icon fontSize="inherit">help_outline</Icon>
                </Tooltip>
              </span>
            )}
          </Box>
          {readOnly ? (
            <Typography variant={`body${typographyVariant}`} color="textSecondary">
              {getValue()}
            </Typography>
          ) : (
            <Select
              autoWidth
              className={classes.input}
              name={props.name}
              value={value}
              multiple={multiple}
              onChange={changeHandle}
              MenuProps={{
                variant: 'menu',
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
              }}
              {...multipleProps}
            >
              {selectAll && multiple ? (
                <MenuItem divider value="all">
                  <Checkbox checked={allSelected ? true : false}></Checkbox>
                  <ListItemText>Seleccionar todos</ListItemText>
                </MenuItem>
              ) : null}
              {selectValues()}
            </Select>
          )}
        </FormControl>
      )
    }
  )
)
